import React, { useCallback, useRef, useState } from "react";

import { useDropzone } from "react-dropzone";

import { cn } from "@utils/ui";

import { FileDropzone } from "./components/FileDropzone";
import { FileList } from "./components/FileList";

/**
 * Props for the FileInput component
 */
export type FileInputProps = {
  // File selection options
  /** Accepted file types (MIME types or extensions like '.pdf', 'image/*') */
  accept?: string | string[];
  /** Allow multiple file selection */
  multiple?: boolean;
  /** Maximum file size in bytes (default: 10MB) */
  maxSize?: number;
  /** Maximum number of files allowed (default: 10 for multiple, 1 for single) */
  maxFiles?: number;
  /** Disable file selection */
  disabled?: boolean;

  // Styling options
  /** Additional CSS class names for the container */
  className?: string;
  /** Additional CSS class names for the dropzone */
  dropzoneClassName?: string;
  /** Size variant of the component */
  size?: "sm" | "default";

  // Callback functions
  /** Called when files are selected and validated */
  onFilesSelected?: (files: File[]) => void;
  /** Called when a file is removed */
  onFileRemove?: (file: File, index: number) => void;
  /** Called when preview button is clicked */
  onPreview?: (file: File, index: number) => void;
  /** Called during file upload progress */
  onUploadProgress?: (file: File, progress: number) => void;
  /** Called when file upload completes successfully */
  onUploadComplete?: (file: File, result?: unknown) => void;
  /** Called when file upload encounters an error */
  onUploadError?: (file: File, error: string) => void;

  // Validation options
  /** Array of allowed file types for validation */
  allowedTypes?: string[];
  /** Custom validation function that returns error message or null */
  customValidator?: (file: File) => string | null;

  // UI options
  /** Placeholder text for the dropzone */
  placeholder?: string;
  /** Show upload progress bars (not yet implemented) */
  showProgress?: boolean;
  /** Show the list of selected files */
  showFileList?: boolean;
  /** Hide the upload button (not yet implemented) */
  hideUploadButton?: boolean;
  /** Pre-populate with existing files */
  existingFiles?: File[] | undefined;
};

/**
 * Internal type for tracking file upload status
 */
type FileWithStatus = {
  /** The file object */
  file: File;
  /** Current upload/processing status */
  status: "pending" | "uploading" | "success" | "error";
  /** Upload progress percentage (0-100) */
  progress: number;
  /** Error message if upload failed */
  error?: string;
  /** Result data from successful upload */
  result?: unknown;
};

/**
 * FileInput component
 *
 * A comprehensive file input component with drag-and-drop support, file validation,
 * and upload progress tracking. Supports single or multiple file selection.
 *
 * Features:
 * - Drag and drop file selection
 * - File type and size validation
 * - Custom validation functions
 * - Preview and remove actions
 * - Upload progress tracking (callbacks provided)
 * - Responsive sizing options
 *
 * @example
 * ```tsx
 * <FileInput
 *   multiple
 *   maxSize={5 * 1024 * 1024}
 *   allowedTypes={['.pdf', '.doc', '.docx']}
 *   onFilesSelected={(files) => console.log('Selected:', files)}
 *   onPreview={(file) => window.open(URL.createObjectURL(file))}
 * />
 * ```
 *
 * @param accept - Accepted file types (MIME types or extensions)
 * @param multiple - Allow multiple file selection (default: false)
 * @param maxSize - Maximum file size in bytes (default: 10MB)
 * @param maxFiles - Maximum number of files allowed
 * @param disabled - Disable file selection (default: false)
 * @param className - Additional CSS class names for the container
 * @param dropzoneClassName - Additional CSS class names for the dropzone
 * @param size - Size variant of the component (default: "default")
 * @param onFilesSelected - Called when files are selected and validated
 * @param onFileRemove - Called when a file is removed
 * @param onPreview - Called when preview button is clicked
 * @param onUploadProgress - Called during file upload progress
 * @param onUploadComplete - Called when file upload completes successfully
 * @param onUploadError - Called when file upload encounters an error
 * @param allowedTypes - Array of allowed file types for validation
 * @param customValidator - Custom validation function that returns error message or null
 * @param placeholder - Placeholder text for the dropzone
 * @param showProgress - Show upload progress bars (not yet implemented)
 * @param showFileList - Show the list of selected files (default: true)
 * @param hideUploadButton - Hide the upload button (not yet implemented)
 * @param existingFiles - Pre-populate with existing files
 */
export const FileInput = ({
  accept,
  multiple = false,
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = multiple ? 10 : 1,
  disabled = false,
  className,
  dropzoneClassName,
  size = "default",
  onFilesSelected,
  onFileRemove,
  onPreview,
  allowedTypes,
  customValidator,
  placeholder = multiple ? "Drag and drop files here" : "Drag and drop a file here",
  showFileList = true,
  existingFiles,
}: FileInputProps) => {
  const [files, setFiles] = useState<FileWithStatus[]>(
    existingFiles?.map((file) => ({
      file,
      status: "success",
      progress: 100,
    })) || []
  );
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File validation
  const validateFile = useCallback(
    (file: File): string | null => {
      // Size validation
      if (file.size > maxSize) {
        return `File size exceeds ${formatFileSize(maxSize)}`;
      }

      // Type validation
      if (allowedTypes && allowedTypes.length > 0) {
        const fileType = file.type;
        const fileExtension = file.name.split(".").pop()?.toLowerCase();

        const isTypeAllowed = allowedTypes.some((type) => {
          if (type.startsWith(".")) {
            return fileExtension === type.slice(1);
          }
          return fileType.includes(type) || fileType === type;
        });

        if (!isTypeAllowed) {
          return `File type not allowed. Accepted types: ${allowedTypes.join(", ")}`;
        }
      }

      // Custom validation
      if (customValidator) {
        return customValidator(file);
      }

      return null;
    },
    [maxSize, allowedTypes, customValidator]
  );

  // Handle file selection
  const handleFileSelection = useCallback(
    (selectedFiles: File[]) => {
      const validFiles: FileWithStatus[] = [];
      const errors: string[] = [];

      selectedFiles.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          errors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push({
            file,
            status: "pending",
            progress: 0,
          });
        }
      });

      // Check max files limit
      const currentFileCount = files.length;
      const newFileCount = validFiles.length;
      if (currentFileCount + newFileCount > maxFiles) {
        errors.push(`Maximum ${maxFiles} files allowed`);
        return;
      }

      if (errors.length > 0) {
        // You might want to show these errors using your notification system
        console.error("File validation errors:", errors);
        return;
      }

      const updatedFiles = multiple ? [...files, ...validFiles] : validFiles;
      setFiles(updatedFiles);
      onFilesSelected?.(validFiles.map((f) => f.file));

      // Reset the input value immediately after processing files
      // This prevents Windows from showing "source and destination file names are the same" dialog
      // when dealing with cloud-first files (OneDrive Files On-Demand)
      // The setTimeout ensures the reset happens after the browser processes the file selection
      setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }, 0);
    },
    [files, multiple, maxFiles, validateFile, onFilesSelected]
  );

  // Dropzone configuration
  const {
    getRootProps,
    getInputProps,
    isDragActive: dropzoneIsDragActive,
  } = useDropzone({
    accept: Array.isArray(accept)
      ? accept.reduce((acc, type) => ({ ...acc, [type]: [] }), {})
      : accept
        ? { [accept]: [] }
        : undefined,
    multiple,
    maxSize,
    disabled,
    onDrop: handleFileSelection,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    // Prevent Windows from showing dialogs for cloud files
    noClick: false,
    noKeyboard: false,
  });

  const isDropzoneHidden = !multiple && files.length > 0;

  // Remove file
  const removeFile = (fileToRemove: File, index: number) => {
    setFiles((prev) => prev.filter((f) => f.file !== fileToRemove));
    onFileRemove?.(fileToRemove, index);
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className={cn("w-full", className)}>
      {/* Dropzone */}
      {!isDropzoneHidden && (
        <FileDropzone
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          fileInputRef={fileInputRef}
          isDragActive={isDragActive || dropzoneIsDragActive}
          disabled={disabled}
          placeholder={placeholder}
          allowedTypes={allowedTypes}
          maxSize={maxSize}
          size={size}
          showSelectButton={files.length === 0}
          dropzoneClassName={dropzoneClassName}
          formatFileSize={formatFileSize}
        />
      )}

      {/* File list */}
      {showFileList && (
        <FileList
          files={files}
          onPreview={onPreview}
          onRemove={removeFile}
          formatFileSize={formatFileSize}
        />
      )}
    </div>
  );
};
